// API 통신 유틸리티 + 토큰 관리
import { getGlobalLoadingFunctions } from '../contexts/LoadingContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:10000/cis-api';

// 토큰 갱신 중복 방지용 Promise
let refreshPromise = null;

// ============ Token Manager ============
export const TokenManager = {
    getAccessToken: () => localStorage.getItem('accessToken'),
    getRefreshToken: () => localStorage.getItem('refreshToken'),
    getUser: () => {
        const user = localStorage.getItem('user');
        return user ? JSON.parse(user) : null;
    },

    setTokens: (accessToken, refreshToken) => {
        localStorage.setItem('accessToken', accessToken);
        if (refreshToken) {
            localStorage.setItem('refreshToken', refreshToken);
        }
    },

    setUser: (user) => {
        localStorage.setItem('user', JSON.stringify(user));
    },

    clear: () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('isLoggedIn');
    },

    isAuthenticated: () => {
        return !!localStorage.getItem('accessToken');
    }
};

// ============ API Request Helper ============
async function apiRequest(endpoint, options = {}) {
    const { silent, ...fetchOptions } = options;
    const { startLoading, stopLoading } = getGlobalLoadingFunctions();

    // 로딩 시작 (silent 모드에서는 스피너 표시하지 않음)
    if (!silent) startLoading();

    try {
        const url = `${API_BASE_URL}${endpoint}`;
        const accessToken = TokenManager.getAccessToken();

        const headers = {
            'Content-Type': 'application/json',
            ...fetchOptions.headers,
        };

        if (accessToken) {
            headers['Authorization'] = `Bearer ${accessToken}`;
        }

        let response = await fetch(url, {
            ...fetchOptions,
            headers,
        });

        // 401 에러 시 토큰 갱신 시도
        if (response.status === 401 && TokenManager.getRefreshToken()) {
            const refreshed = await refreshAccessToken();
            if (refreshed) {
                // 갱신 성공 시 원래 요청 재시도
                headers['Authorization'] = `Bearer ${TokenManager.getAccessToken()}`;
                response = await fetch(url, {
                    ...fetchOptions,
                    headers,
                });
            }
        }

        const data = await response.json();

        if (!response.ok) {
            const error = new Error(data.message || '요청 처리 중 오류가 발생했습니다.');
            error.status = response.status;
            error.data = data;
            throw error;
        }

        return data;
    } finally {
        // 로딩 종료 (성공/실패 모두)
        if (!silent) stopLoading();
    }
}

// 토큰 갱신 (중복 호출 방지)
async function refreshAccessToken() {
    const refreshToken = TokenManager.getRefreshToken();
    if (!refreshToken) return false;

    // 이미 갱신 중이면 기존 Promise 재사용
    if (refreshPromise) {
        return refreshPromise;
    }

    refreshPromise = (async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ refreshToken }),
            });

            if (response.ok) {
                const data = await response.json();
                TokenManager.setTokens(data.accessToken, data.refreshToken);

                // 토큰 갱신 시 권한 재로드 이벤트 발생
                window.dispatchEvent(new CustomEvent('tokenRefreshed'));

                return true;
            }
        } catch (error) {
            console.error('Token refresh failed:', error);
        }

        TokenManager.clear();
        window.dispatchEvent(new CustomEvent('authCleared'));
        return false;
    })();

    try {
        return await refreshPromise;
    } finally {
        refreshPromise = null;
    }
}

// ============ Auth API ============
export const AuthAPI = {
    // 회원가입 전 인증코드 발송
    sendVerification: async (email) => {
        return apiRequest('/auth/send-verification', {
            method: 'POST',
            body: JSON.stringify({ email }),
        });
    },

    // 회원가입 전 인증코드 검증
    verifyEmail: async (email, code) => {
        return apiRequest('/auth/verify-email', {
            method: 'POST',
            body: JSON.stringify({ email, code }),
        });
    },

    // 회원가입
    register: async (userData) => {
        return apiRequest('/auth/register', {
            method: 'POST',
            body: JSON.stringify(userData),
        });
    },

    // 로그인
    login: async (email, password) => {
        const data = await apiRequest('/auth/login', {
            method: 'POST',
            body: JSON.stringify({ email, password }),
        });

        // 토큰 및 사용자 정보 저장
        TokenManager.setTokens(data.accessToken, data.refreshToken);
        TokenManager.setUser(data.user);
        localStorage.setItem('isLoggedIn', 'true');

        // 로그인 성공 시 권한 재로드 이벤트 발생
        window.dispatchEvent(new CustomEvent('tokenRefreshed'));

        return data;
    },

    // 로그아웃
    logout: async () => {
        const refreshToken = TokenManager.getRefreshToken();
        if (refreshToken) {
            try {
                await fetch(`${API_BASE_URL}/auth/logout`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ refreshToken }),
                });
            } catch (error) {
                console.error('Logout API call failed:', error);
            }
        }
        TokenManager.clear();
    },

    // 현재 사용자 정보 조회
    getCurrentUser: async () => {
        return apiRequest('/auth/me', {
            method: 'GET',
        });
    },
};

// ============ Email API ============
export const EmailAPI = {
    // 인증 이메일 발송
    sendVerification: async () => {
        return apiRequest('/email/send-verification', {
            method: 'POST',
        });
    },

    // 이메일 인증 확인
    verify: async (code) => {
        return apiRequest('/email/verify', {
            method: 'POST',
            body: JSON.stringify({ code }),
        });
    },

    // 인증 이메일 재발송
    resend: async () => {
        return apiRequest('/email/resend-verification', {
            method: 'POST',
        });
    },

    // 이메일 인증 상태 확인
    getStatus: async () => {
        return apiRequest('/email/verification-status', {
            method: 'GET',
        });
    },
};

// ============ Users API ============
export const UsersAPI = {
    // 전체 회원 목록 조회
    getUsers: async (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        const endpoint = queryString ? `/users?${queryString}` : '/users';
        return apiRequest(endpoint, {
            method: 'GET',
        });
    },

    // 회원 상세 조회
    getUser: async (id) => {
        return apiRequest(`/users/${id}`, {
            method: 'GET',
        });
    },

    // 회원 승인
    approveUser: async (id) => {
        return apiRequest(`/users/${id}/approve`, {
            method: 'PATCH',
        });
    },

    // 회원 반려
    rejectUser: async (id, reason) => {
        return apiRequest(`/users/${id}/reject`, {
            method: 'PATCH',
            body: JSON.stringify({ reason }),
        });
    },

    // 회원 정보 수정
    updateUser: async (id, data) => {
        return apiRequest(`/users/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    },

    // 회원 삭제
    deleteUser: async (id) => {
        return apiRequest(`/users/${id}`, {
            method: 'DELETE',
        });
    },
};

// ============ Roles API ============
export const RolesAPI = {
    // 전체 역할 조회
    getRoles: async () => {
        return apiRequest('/roles', {
            method: 'GET',
        });
    },

    // 역할 생성
    createRole: async (data) => {
        return apiRequest('/roles', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    // 역할 수정
    updateRole: async (id, data) => {
        return apiRequest(`/roles/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    },

    // 역할 삭제
    deleteRole: async (id) => {
        return apiRequest(`/roles/${id}`, {
            method: 'DELETE',
        });
    },

    // 사용자에게 역할 부여
    assignRole: async (userId, roleId) => {
        return apiRequest('/roles/assign', {
            method: 'POST',
            body: JSON.stringify({ userId, roleId }),
        });
    },

    // 사용자의 역할 해제
    revokeRole: async (userId, roleId) => {
        return apiRequest('/roles/revoke', {
            method: 'POST',
            body: JSON.stringify({ userId, roleId }),
        });
    },

    // 역할별 섹션 권한 설정
    setSectionPermission: async (roleId, sectionId, canAccess) => {
        return apiRequest('/roles/permissions/section', {
            method: 'POST',
            body: JSON.stringify({ roleId, sectionId, canAccess }),
        });
    },

    // 역할별 카테고리 권한 설정
    setCategoryPermission: async (roleId, categoryId, canAccess) => {
        return apiRequest('/roles/permissions/category', {
            method: 'POST',
            body: JSON.stringify({ roleId, categoryId, canAccess }),
        });
    },

    // 역할별 파트 권한 설정
    setPartPermission: async (roleId, partId, permissions) => {
        return apiRequest('/roles/permissions/part', {
            method: 'POST',
            body: JSON.stringify({
                roleId,
                partId,
                canView: permissions.canView || false,
                canCreate: permissions.canCreate || false,
                canEdit: permissions.canEdit || false,
                canDelete: permissions.canDelete || false,
                canUpload: permissions.canUpload || false,
            }),
        });
    },

    // 역할별 권한 조회
    getRolePermissions: async (roleId) => {
        return apiRequest(`/roles/${roleId}/permissions`, {
            method: 'GET',
        });
    },
};

// ============ Contents API ============
export const ContentsAPI = {
    // 메뉴 구조 조회 (섹션 > 카테고리 > 파트)
    getMenu: async ({ silent = false, includeEmpty = false } = {}) => {
        const query = includeEmpty ? '?includeEmpty=true' : '';
        return apiRequest(`/content/menu${query}`, {
            method: 'GET',
            silent,
        });
    },

    // 섹션 상세 조회
    getSectionById: async (sectionId) => {
        return apiRequest(`/content/sections/${sectionId}`, {
            method: 'GET',
        });
    },

    // 파트 상세 조회
    getPartById: async (partId) => {
        return apiRequest(`/content/parts/${partId}`, {
            method: 'GET',
        });
    },

    // 카테고리 생성
    createCategory: async (data) => {
        return apiRequest('/content/categories', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    // 카테고리 수정
    updateCategory: async (id, data) => {
        return apiRequest(`/content/categories/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    },

    // 카테고리 삭제
    deleteCategory: async (id) => {
        return apiRequest(`/content/categories/${id}`, {
            method: 'DELETE',
        });
    },

    // 파트 생성
    createPart: async (data) => {
        return apiRequest('/content/parts', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    // 파트 수정
    updatePart: async (id, data) => {
        return apiRequest(`/content/parts/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    },

    // 파트 삭제
    deletePart: async (id) => {
        return apiRequest(`/content/parts/${id}`, {
            method: 'DELETE',
        });
    },
};

// ============ Uploads API ============
export const UploadsAPI = {
    // 업로드 생성 (메타데이터 저장, PENDING 상태)
    createUpload: async (data) => {
        return apiRequest('/uploads', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    // 업로드 완료 처리 (COMPLETED 상태로 변경)
    completeUpload: async (data) => {
        return apiRequest('/uploads/complete', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    // 파트별 업로드 목록 조회
    getUploadsByPartId: async (partId, params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        const endpoint = queryString
            ? `/uploads/part/${partId}?${queryString}`
            : `/uploads/part/${partId}`;
        return apiRequest(endpoint, {
            method: 'GET',
        });
    },

    // 업로드 상세 조회
    getUploadById: async (id) => {
        return apiRequest(`/uploads/${id}`, {
            method: 'GET',
        });
    },

    // 업로드 삭제
    deleteUpload: async (id) => {
        return apiRequest(`/uploads/${id}`, {
            method: 'DELETE',
        });
    },

    // 다운로드 기록
    recordDownload: async (id) => {
        return apiRequest(`/uploads/${id}/download`, {
            method: 'POST',
        });
    },

    // 내 업로드 목록 조회
    getMyUploads: async (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        const endpoint = queryString ? `/uploads/my?${queryString}` : '/uploads/my';
        return apiRequest(endpoint, {
            method: 'GET',
        });
    },

    // 그룹별 업로드 목록 조회
    getUploadsByGroupId: async (groupId, params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        const endpoint = queryString
            ? `/uploads/group/${groupId}?${queryString}`
            : `/uploads/group/${groupId}`;
        return apiRequest(endpoint, {
            method: 'GET',
        });
    },

    // 파일 순서 일괄 수정
    updateDisplayOrders: async (orders) => {
        return apiRequest('/uploads/display-orders', {
            method: 'PATCH',
            body: JSON.stringify({ orders }),
        });
    },
};

// ============ Upload Groups API ============
export const UploadGroupsAPI = {
    // 파트별 자료 그룹 목록 조회
    getByPartId: async (partId, { silent = false } = {}) => {
        return apiRequest(`/upload-groups/part/${partId}`, {
            method: 'GET',
            silent,
        });
    },

    // 자료 그룹 상세 조회
    getById: async (groupId) => {
        return apiRequest(`/upload-groups/${groupId}`, {
            method: 'GET',
        });
    },

    // 자료 그룹 생성
    create: async (data) => {
        return apiRequest('/upload-groups', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    // 자료 그룹 수정
    update: async (groupId, data) => {
        return apiRequest(`/upload-groups/${groupId}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    },

    // 자료 그룹 삭제
    delete: async (groupId) => {
        return apiRequest(`/upload-groups/${groupId}`, {
            method: 'DELETE',
        });
    },
};

// ============ Master API ============
export const MasterAPI = {
    // 소속 목록 조회
    getAffiliations: async () => {
        return apiRequest('/master/affiliations', {
            method: 'GET',
        });
    },

    // 본부 목록 조회
    getHeadquarters: async () => {
        return apiRequest('/master/headquarters', {
            method: 'GET',
        });
    },
};

// ============ S3 API ============
export const S3API = {
    // 업로드용 Presigned URL 발급 (10분 유효)
    getUploadUrl: async (uploadId, contentType) => {
        return apiRequest('/s3/upload-url', {
            method: 'POST',
            body: JSON.stringify({ uploadId, contentType }),
        });
    },

    // 다운로드용 Presigned URL 발급 (1시간 유효)
    getDownloadUrl: async (uploadId, urlType = 'DOWNLOAD') => {
        return apiRequest('/s3/download-url', {
            method: 'POST',
            body: JSON.stringify({ uploadId, urlType }),
        });
    },

    // S3 키로 직접 Presigned URL 발급 (1시간 유효)
    getPresignedUrlByKey: async (s3Key) => {
        return apiRequest('/s3/presign-key', {
            method: 'POST',
            body: JSON.stringify({ s3Key }),
        });
    },

    // S3 설정 정보 조회
    getConfig: async () => {
        return apiRequest('/s3/config', {
            method: 'GET',
        });
    },

    // 배치 Presigned URL 발급 (썸네일 일괄 로딩용, silent)
    batchPresign: async (items) => {
        return apiRequest('/s3/batch-presign', {
            method: 'POST',
            body: JSON.stringify({ items }),
            silent: true,
        });
    },
};

// ============ S3 Direct Upload Utility ============
export const uploadFileToS3 = (presignedUrl, file, onProgress) => {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable && onProgress) {
                const percentComplete = Math.round((event.loaded / event.total) * 100);
                onProgress(percentComplete);
            }
        });

        xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve({ success: true });
            } else {
                reject(new Error(`Upload failed with status ${xhr.status}`));
            }
        });

        xhr.addEventListener('error', () => {
            reject(new Error('Upload failed'));
        });

        xhr.addEventListener('abort', () => {
            reject(new Error('Upload aborted'));
        });

        xhr.open('PUT', presignedUrl);
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
        xhr.send(file);
    });
};

export default apiRequest;
